export const MODEL_VIEWER_PEDESTAL_IDS = ['none', 'cylinder', 'box'] as const;
export const MODEL_VIEWER_PEDESTAL_MATERIAL_IDS = [
    'solid',
    'glass',
    'marble',
] as const;

export type ModelViewerPedestal = typeof MODEL_VIEWER_PEDESTAL_IDS[number];
export type ModelViewerPedestalMaterial = (
    typeof MODEL_VIEWER_PEDESTAL_MATERIAL_IDS[number]
);
export type ModelViewerPedestalShape = Exclude<ModelViewerPedestal, 'none'>;

export const DEFAULT_MODEL_VIEWER_PEDESTAL:ModelViewerPedestal = 'none';
export const DEFAULT_MODEL_VIEWER_PEDESTAL_MATERIAL:ModelViewerPedestalMaterial = (
    'solid'
);

export interface ModelViewerPedestalBounds {
    depth: number
    height: number
    radius: number
    width: number
}

export interface ModelViewerPedestalLayout {
    cameraDistanceScale: number
    centerY: number
    depth: number
    floorY: number
    height: number
    radius: number
    shape: ModelViewerPedestalShape
    topY: number
    width: number
}

const PEDESTAL_BOUNDS_EPSILON = 0.001;
const PEDESTAL_CAMERA_DISTANCE_SCALE_MAX = 1.35;
const PEDESTAL_BOX_HEIGHT_RATIO = 0.1;
const PEDESTAL_CYLINDER_HEIGHT_RATIO = 0.14;
const PEDESTAL_FOOTPRINT_PADDING_RATIO = 0.08;
const PEDESTAL_RADIUS_PADDING_RATIO = 0.1;

export const isModelViewerPedestal = (
    value:unknown,
):value is ModelViewerPedestal => (
    MODEL_VIEWER_PEDESTAL_IDS.includes(value as ModelViewerPedestal)
);

export const isModelViewerPedestalMaterial = (
    value:unknown,
):value is ModelViewerPedestalMaterial => (
    MODEL_VIEWER_PEDESTAL_MATERIAL_IDS.includes(
        value as ModelViewerPedestalMaterial,
    )
);

/**
 * Sizes a studio pedestal from the centered product bounds. Width and depth
 * are used instead of the bounding-sphere radius alone so tall garments do
 * not receive an oversized platform.
 */
export function getModelViewerPedestalLayout(
    bounds:ModelViewerPedestalBounds,
    pedestal:ModelViewerPedestal,
):ModelViewerPedestalLayout|undefined {
    if(pedestal === 'none') return undefined;

    const largestFootprintDimension = Math.max(
        bounds.width,
        bounds.depth,
        bounds.radius * 0.1,
        PEDESTAL_BOUNDS_EPSILON,
    );
    const smallestUsefulDimension = Math.max(
        largestFootprintDimension * 0.04,
        PEDESTAL_BOUNDS_EPSILON,
    );
    const productWidth = Math.max(bounds.width, smallestUsefulDimension);
    const productDepth = Math.max(bounds.depth, smallestUsefulDimension);
    const padding = Math.max(
        largestFootprintDimension * PEDESTAL_FOOTPRINT_PADDING_RATIO,
        bounds.radius * PEDESTAL_RADIUS_PADDING_RATIO,
    );
    const boxWidth = productWidth + padding * 2;
    const boxDepth = productDepth + padding * 2;
    const cylinderRadius = Math.hypot(productWidth, productDepth) / 2 + padding;
    const width = pedestal === 'cylinder' ? cylinderRadius * 2 : boxWidth;
    const depth = pedestal === 'cylinder' ? cylinderRadius * 2 : boxDepth;
    const heightRatio = pedestal === 'cylinder'
        ? PEDESTAL_CYLINDER_HEIGHT_RATIO
        : PEDESTAL_BOX_HEIGHT_RATIO;
    const height = Math.max(
        Math.max(width, depth) * heightRatio,
        PEDESTAL_BOUNDS_EPSILON,
    );
    const topY = -bounds.height / 2;
    const floorY = topY - height;

    return {
        cameraDistanceScale: Math.min(
            PEDESTAL_CAMERA_DISTANCE_SCALE_MAX,
            1 + height / Math.max(bounds.radius, PEDESTAL_BOUNDS_EPSILON),
        ),
        centerY: topY - height / 2,
        depth,
        floorY,
        height,
        radius: pedestal === 'cylinder'
            ? cylinderRadius
            : Math.hypot(boxWidth, boxDepth) / 2,
        shape: pedestal,
        topY,
        width,
    };
}
